// Streak / perfect day / level-up as big movie-title text in her room (D_Desk `title`): no popup and no R screen on
// desktop. Fraunces 78 in her color with a glow, her line under it, the week's seven dots for a streak. Each beat goes
// by itself after 7 s; a click shows the next one or ends it.
import type { CharId, StreakState } from '@shared/types';
import { Icon, type IconName } from '../../components';
import { colors } from '../../theme';
import { TITLE } from './roomCopy';
import s from './room.module.css';

export type BeatKind = 'streak' | 'perfect' | 'level';
export interface TitleBeat { kind: BeatKind; big: string; sub: string }

const ICON: Record<BeatKind, IconName> = { streak: 'flame', perfect: 'gift', level: 'star' };
const WD = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

interface Props { ch: CharId; vw: number; beats: TitleBeat[]; i: number; week: StreakState['week']; today: string; first: boolean; onNext: () => void }

export function RoomTitle({ ch, vw, beats, i, week, today, first, onNext }: Props) {
  const c = colors(ch);
  const late = first && i === 0 ? 700 : 0;
  const big = beats.some((b) => b.kind !== 'streak');
  const CONF = ['#F5F1EA', c.acc, '#7FC8A9', '#9DB7F5', '#F28B82', c.acc];
  const x0 = (vw - 920) / 2 + 40;
  const pieces = Array.from({ length: big ? 18 : 10 }, (_, k) => ({
    x: x0 + ((k * 53) % 900), w: 7 + (k % 3) * 2, h: 11 + (k % 4) * 3, color: CONF[k % CONF.length], dur: 1900 + (k % 5) * 260, delay: late + 120 + (k % 7) * 110,
  }));
  const b = beats[i];
  if (!b) return null;
  return (
    <div class={s.titleLayer}>
      <button type="button" class={s.titleBack} aria-label="Continue" onClick={onNext} style={{ animationDelay: `${late}ms` }} />
      <div class={s.confetti} aria-hidden="true">
        {pieces.map((p, k) => (
          <span key={`${i}-${k}`} style={{ left: p.x, width: p.w, height: p.h, background: p.color, animation: `xmConfetti ${p.dur}ms ${p.delay}ms ease-in both` }} />
        ))}
      </div>
      <div key={i} class={s.titleSlide} role="status" style={{ left: x0, animationDelay: `${late}ms` }}>
        <div class={s.titleIcon}>
          <Icon name={ICON[b.kind]} size={72} strokeWidth={1.1} color={c.acc} fill={b.kind === 'streak' ? c.acc : 'rgba(255,255,255,0.08)'} style={{ filter: `drop-shadow(0 0 22px ${c.acc})` }} />
        </div>
        <h2 class={s.titleBig}>{b.big}</h2>
        <p class={s.titleSub}>{b.sub}</p>
        {b.kind === 'streak' && (
          <div class={s.titleWeek} aria-hidden="true">
            {week.map((d, k) => {
              const on = d.mark === 'lit' || d.mark === 'rest' || d.mark === 'freeze';
              const isToday = d.day === today;
              return (
                <span key={d.day} class={s.titleDay}>
                  <span style={{ color: isToday ? c.acc : '#E9E4DA' }}>{d.label || WD[k]}</span>
                  <span class={s.titleDot} style={{ background: on ? c.acc : 'rgba(255,255,255,0.12)', boxShadow: isToday ? `0 0 0 3px rgba(20,17,13,0.9), 0 0 0 5px ${c.acc}` : 'none' }}>
                    <Icon name="check" size={13} strokeWidth={3} color={on ? c.on : 'transparent'} />
                  </span>
                </span>
              );
            })}
          </div>
        )}
        <p class={s.titleHint}>{i < beats.length - 1 ? TITLE.more : TITLE.back}</p>
      </div>
    </div>
  );
}
